import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { toast } from "sonner";
import { useAnalytics, AnalyticsEvents, UpgradeTriggers } from "@/hooks/useAnalytics";
import {
  Globe, Map, TrendingUp, Award, Camera, Heart, Users, Sparkles,
  Crown, Shield, Clock, X, Check, ChevronRight, Zap, Star, 
  MapPin, Calendar, Lock, Unlock, Gift, BadgeCheck, Timer
} from "lucide-react";

// ==================== PAYWALL TYPES ====================

export const PaywallType = {
  WORLD_MAP: "world_map",
  TRAVEL_RANK: "travel_rank", 
  MEMORY_VAULT: "memory_vault",
  DUO_UPSELL: "duo_upsell",
  FEATURE_GATE: "feature_gate",
  TRIP_SUGGESTIONS: "trip_suggestions",
  MILESTONE_PREDICTIONS: "milestone_predictions",
  ROUTE_REPLAY: "route_replay",
  CITY_HEATMAP: "city_heatmap",
  STREAK_SAVE: "streak_save",
  YEARLY_GROWTH: "yearly_growth",
  ADVANCED_FILTERS: "advanced_filters",
  BUDGET_TRACKER: "budget_tracker",
};

// ==================== PAYWALL CONTENT ====================

const PAYWALL_CONTENT = {
  [PaywallType.WORLD_MAP]: {
    trigger: UpgradeTriggers.MAP_LAYERS,
    triggerSources: ["city_heatmaps", "world_percentile", "timeline_replay", "premium_skins"],
    heroGradient: "from-blue-600 via-indigo-600 to-purple-700",
    heroIcon: Globe,
    heroImage: "world_map",
    defaultPlan: "explorer_pro",
    variants: {
      A: {
        headline: "Unlock your full travel story",
        subheadline: "See your world in deeper detail with city heatmaps, yearly replays, and exclusive map themes.",
      },
      B: {
        headline: "See where your journey really stands",
        subheadline: "Discover hidden patterns in your travels with premium map analytics and beautiful themes.",
      },
      C: {
        headline: "Reveal the world behind your map",
        subheadline: "Go beyond pins — unlock city heatmaps, travel replays, and stunning map customizations.",
      },
    },
    benefits: [
      { icon: MapPin, text: "City-level heatmaps" },
      { icon: Calendar, text: "Yearly travel replay" },
      { icon: Sparkles, text: "Premium map themes" },
      { icon: TrendingUp, text: "World percentile insights" },
      { icon: Star, text: "AI trip suggestions" },
    ],
    cta: {
      trial: "Learn more on duolyst.co.uk",
      upgrade: "Upgrade on duolyst.co.uk",
    },
  },
  
  [PaywallType.TRAVEL_RANK]: {
    trigger: UpgradeTriggers.RANK_INSIGHTS,
    triggerSources: ["global_percentile", "advanced_score", "milestone_forecast", "rank_history"],
    heroGradient: "from-amber-500 via-orange-500 to-rose-600",
    heroIcon: Award,
    heroImage: "rank_badge",
    defaultPlan: "explorer_pro",
    variants: {
      A: {
        headline: "Level up your explorer identity",
        subheadline: "Reveal your percentile, unlock elite badge tiers, and see how fast your journey is growing.",
      },
      B: {
        headline: "See how far ahead you really are",
        subheadline: "Compare your progress globally, track your rank over time, and predict your next milestone.",
      },
      C: {
        headline: "Unlock your legendary travel rank",
        subheadline: "Access elite rank insights, beautiful badge frames, and AI recommendations to level up faster.",
      },
    },
    benefits: [
      { icon: Globe, text: "Global percentile ranking" },
      { icon: TrendingUp, text: "Rank history charts" },
      { icon: Zap, text: "Milestone predictions" },
      { icon: Crown, text: "Premium rank frames" },
      { icon: Sparkles, text: "AI level-up recommendations" },
    ],
    cta: {
      trial: "Learn more on duolyst.co.uk",
      upgrade: "Upgrade on duolyst.co.uk",
    },
  },
  
  [PaywallType.MEMORY_VAULT]: {
    trigger: UpgradeTriggers.MEMORY_VAULT,
    triggerSources: ["memory_limit_exceeded", "scrapbook_themes", "anniversary_vault"],
    heroGradient: "from-rose-500 via-pink-500 to-fuchsia-600",
    heroIcon: Camera,
    heroImage: "scrapbook",
    defaultPlan: "explorer_pro",
    variants: {
      A: {
        headline: "Never lose a single memory",
        subheadline: "Keep every trip photo, story, and shared milestone beautifully saved forever.",
      },
      B: {
        headline: "Save every moment together",
        subheadline: "Unlimited storage for your adventures — photos, notes, and memories that last a lifetime.",
      },
      C: {
        headline: "Turn trips into a forever story",
        subheadline: "Create beautiful scrapbooks, timelines, and anniversary vaults with unlimited space.",
      },
    },
    benefits: [
      { icon: Camera, text: "Unlimited memories" },
      { icon: Sparkles, text: "Beautiful scrapbook themes" },
      { icon: Heart, text: "Anniversary vault" },
      { icon: Calendar, text: "Timeline playback" },
      { icon: Gift, text: "Memory widgets" },
    ],
    cta: {
      trial: "Learn more on duolyst.co.uk",
      upgrade: "Upgrade on duolyst.co.uk",
    },
  },
  
  [PaywallType.DUO_UPSELL]: {
    trigger: UpgradeTriggers.PARTNER_LINK_DUO,
    triggerSources: ["partner_link", "first_shared_trip", "couple_milestone"],
    heroGradient: "from-emerald-500 via-teal-500 to-cyan-600",
    heroIcon: Users,
    heroImage: "couple_map",
    defaultPlan: "duo",
    savingsBadge: "Save together",
    variants: {
      A: {
        headline: "Unlock your shared world together",
        subheadline: "Explorer Pro for both of you, shared maps, Duo badges, anniversary vaults, and joint rank — all for less than 2 solo plans.",
      },
      B: {
        headline: "Build your life map as a team",
        subheadline: "Everything you love about Explorer Pro, now shared with your partner. One price, two explorers.",
      },
      C: {
        headline: "Everything is better together",
        subheadline: "Unlock shared heatmaps, joint rankings, couple badges, and exclusive Duo features for both of you.",
      },
      D: {
        headline: "Upgrade your story as a couple",
        subheadline: "Premium features for two at one beautiful price. Shared maps, memories, and milestones forever.",
      },
    },
    benefits: [
      { icon: Users, text: "Explorer Pro for both" },
      { icon: Map, text: "Shared world heatmaps" },
      { icon: Award, text: "Joint world rank" },
      { icon: Heart, text: "Anniversary vault" },
      { icon: BadgeCheck, text: "Duo legendary badges" },
      { icon: Shield, text: "Joint streak protection" },
    ],
    cta: {
      trial: "Learn more on duolyst.co.uk",
      upgrade: "Upgrade on duolyst.co.uk",
    },
  },
  
  [PaywallType.FEATURE_GATE]: {
    trigger: UpgradeTriggers.MAP_LAYERS,
    triggerSources: ["locked_feature", "premium_feature"],
    heroGradient: "from-coral via-rose-500 to-pink-600",
    heroIcon: Sparkles,
    heroImage: "premium_feature",
    defaultPlan: "explorer_pro",
    variants: {
      A: { headline: "Unlock Premium Features", subheadline: "Get access to advanced analytics, insights, and exclusive tools to enhance your travel journey." },
      B: { headline: "Elevate Your Experience", subheadline: "Discover powerful features designed to help you explore smarter and track your adventures better." },
      C: { headline: "Go Pro with Premium", subheadline: "Unlock the full potential of your travel tracking with premium-only features and insights." },
    },
    benefits: [
      { icon: TrendingUp, text: "Advanced analytics" },
      { icon: MapPin, text: "City-level insights" },
      { icon: Sparkles, text: "AI-powered predictions" },
      { icon: Calendar, text: "Travel timeline replay" },
      { icon: Star, text: "Premium map features" },
    ],
    cta: { trial: "Learn more on duolyst.co.uk", upgrade: "Upgrade on duolyst.co.uk" },
  },
  [PaywallType.TRIP_SUGGESTIONS]: {
    trigger: UpgradeTriggers.MAP_LAYERS,
    triggerSources: ["trip_suggestions"],
    heroGradient: "from-emerald-500 via-teal-500 to-cyan-600",
    heroIcon: MapPin,
    heroImage: "trip_suggestions",
    defaultPlan: "explorer_pro",
    variants: {
      A: { headline: "AI Trip Suggestions", subheadline: "Get personalised destination recommendations based on your travel style and bucket list." },
      B: { headline: "Discover Your Next Trip", subheadline: "Let AI analyse your preferences to recommend perfect destinations you haven't considered." },
      C: { headline: "Smart Trip Planning", subheadline: "Unlock AI-powered trip suggestions tailored to your travel history and dream destinations." },
    },
    benefits: [
      { icon: MapPin, text: "Personalised destinations" },
      { icon: Sparkles, text: "AI-powered recommendations" },
      { icon: TrendingUp, text: "Budget-matched suggestions" },
      { icon: Calendar, text: "Best time to visit" },
      { icon: Star, text: "Hidden gem discoveries" },
    ],
    cta: { trial: "Learn more on duolyst.co.uk", upgrade: "Upgrade on duolyst.co.uk" },
  },
  [PaywallType.MILESTONE_PREDICTIONS]: {
    trigger: UpgradeTriggers.MAP_LAYERS,
    triggerSources: ["milestone_predictions"],
    heroGradient: "from-violet-500 via-purple-500 to-indigo-600",
    heroIcon: TrendingUp,
    heroImage: "milestone_predictions",
    defaultPlan: "explorer_pro",
    variants: {
      A: { headline: "Next Milestone Predictions", subheadline: "See when you'll hit your next travel milestone and what achievements await you." },
      B: { headline: "Your Travel Future", subheadline: "AI predicts your upcoming milestones and helps you plan the fastest route to your goals." },
      C: { headline: "Milestone Forecasting", subheadline: "Unlock predictions for your next country, continent, and achievement milestones." },
    },
    benefits: [
      { icon: TrendingUp, text: "Progress predictions" },
      { icon: Star, text: "Achievement forecasting" },
      { icon: MapPin, text: "Next destination insights" },
      { icon: Calendar, text: "Timeline projections" },
      { icon: Sparkles, text: "Personalised goals" },
    ],
    cta: { trial: "Learn more on duolyst.co.uk", upgrade: "Upgrade on duolyst.co.uk" },
  },
  [PaywallType.ROUTE_REPLAY]: {
    trigger: UpgradeTriggers.MAP_LAYERS,
    triggerSources: ["route_replay"],
    heroGradient: "from-orange-500 via-amber-500 to-yellow-500",
    heroIcon: Calendar,
    heroImage: "route_replay",
    defaultPlan: "explorer_pro",
    variants: {
      A: { headline: "Route Replay", subheadline: "Relive your travel journey with an animated timeline of every trip you've taken." },
      B: { headline: "Replay Your Adventures", subheadline: "Watch your travel story unfold on a beautiful animated map of your journeys." },
      C: { headline: "Travel Timeline Replay", subheadline: "Unlock animated route replay and share your travel story with friends and family." },
    },
    benefits: [
      { icon: Calendar, text: "Animated journey replay" },
      { icon: MapPin, text: "Route visualisation" },
      { icon: Star, text: "Shareable travel stories" },
      { icon: TrendingUp, text: "Year-by-year progression" },
      { icon: Sparkles, text: "Highlight reels" },
    ],
    cta: { trial: "Learn more on duolyst.co.uk", upgrade: "Upgrade on duolyst.co.uk" },
  },
  [PaywallType.CITY_HEATMAP]: {
    trigger: UpgradeTriggers.MAP_LAYERS,
    triggerSources: ["city_heatmap"],
    heroGradient: "from-rose-500 via-pink-500 to-red-600",
    heroIcon: MapPin,
    heroImage: "city_heatmap",
    defaultPlan: "explorer_pro",
    variants: {
      A: { headline: "City Heatmap", subheadline: "See where you've spent the most time with a beautiful city-level heatmap of your travels." },
      B: { headline: "Your City Footprint", subheadline: "Visualise your travel density across cities and discover patterns in your journeys." },
      C: { headline: "City-Level Analytics", subheadline: "Unlock detailed city heatmaps showing your travel concentration and exploration patterns." },
    },
    benefits: [
      { icon: MapPin, text: "City-level heatmap" },
      { icon: TrendingUp, text: "Visit frequency data" },
      { icon: Star, text: "Most visited cities" },
      { icon: Sparkles, text: "Exploration score" },
      { icon: Calendar, text: "City visit timeline" },
    ],
    cta: { trial: "Learn more on duolyst.co.uk", upgrade: "Upgrade on duolyst.co.uk" },
  },
  [PaywallType.BUDGET_TRACKER]: {
    trigger: UpgradeTriggers.MAP_LAYERS,
    triggerSources: ["budget_tracker"],
    heroGradient: "from-green-500 via-emerald-500 to-teal-600",
    heroIcon: TrendingUp,
    heroImage: "budget_tracker",
    defaultPlan: "explorer_pro",
    variants: {
      A: { headline: "Smart Trip Budgets", subheadline: "Track spending, set budgets, and split costs with your travel partner in real time." },
      B: { headline: "Never Overspend Again", subheadline: "Keep every trip on budget with automatic expense tracking and spending insights." },
      C: { headline: "Travel Budget Pro", subheadline: "Unlock smart budgets with multi-currency tracking, cost splitting, and spending analytics." },
    },
    benefits: [
      { icon: TrendingUp, text: "Real-time expense tracking" },
      { icon: Star, text: "Multi-currency support" },
      { icon: MapPin, text: "Per-destination budgets" },
      { icon: Calendar, text: "Daily spending breakdown" },
      { icon: Sparkles, text: "Partner cost splitting" },
    ],
    cta: { trial: "Learn more on duolyst.co.uk", upgrade: "Upgrade on duolyst.co.uk" },
  },
};

// ==================== PRICING ====================

const PRICING = {
  explorer_pro: {
    monthly: { price: 3.99, display: "£3.99" },
    yearly: { price: 29.99, display: "£29.99", savings: "Save 37%" },
  },
  duo: {
    monthly: { price: 5.99, display: "£5.99" },
    yearly: { price: 47.99, display: "£47.99", savings: "Save 33%" },
  },
};

// ==================== MAIN COMPONENT ====================

export const PaywallScreen = ({
  isOpen,
  onClose,
  type = PaywallType.WORLD_MAP,
  triggerSource = "locked_feature",
  hasPartner = false,
  onUpgradeSuccess = null,
}) => {
  const navigate = useNavigate();
  const { trackPaywallImpression, trackPaywallDismiss, trackConversion, trackMonetization } = useAnalytics();
  
  const [variant, setVariant] = useState("A");
  const [variantLoaded, setVariantLoaded] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState("yearly");
  const [loading, setLoading] = useState(false);
  const [webCheckoutLoading, setWebCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);

  // Reset loading states when returning from Safari checkout (iOS)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        setLoading(false);
        setWebCheckoutLoading(false);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);
  const [smartDefaults, setSmartDefaults] = useState(null);
  const [anchoring, setAnchoring] = useState(null);
  const isNativeApp = typeof window !== 'undefined' && !!window.isNativeApp;
  
  const content = PAYWALL_CONTENT[type] || PAYWALL_CONTENT[PaywallType.FEATURE_GATE];
  const variantContent = content.variants[variant] || content.variants.A;

  // Initialize with smart defaults
  useEffect(() => {
    if (isOpen) {
      setVariantLoaded(false);
      fetchOptimizationData();
    }
  }, [isOpen, type, hasPartner]);

  const fetchOptimizationData = async () => {
    try {
      const [variantRes, defaultsRes] = await Promise.all([
        axios.get(`${API}/monetization/variant`).catch(() => ({ data: { variant: "control" } })),
        axios.get(`${API}/pricing/smart-defaults`).catch(() => ({ data: null })),
      ]);
      
      // Set smart defaults
      if (defaultsRes.data) {
        setSmartDefaults(defaultsRes.data);
        // Use smart defaults for initial selection
        const recommendedPlan = type === PaywallType.DUO_UPSELL 
          ? "duo" 
          : (hasPartner ? defaultsRes.data.recommended_plan : "explorer_pro");
        setSelectedPlan(recommendedPlan);
        setBillingCycle(defaultsRes.data.recommended_cycle || "yearly");
        
        // Fetch price anchoring for recommended plan
        fetchAnchoring(recommendedPlan);
      } else {
        // Fallback defaults
        const defaultPlan = type === PaywallType.DUO_UPSELL || hasPartner ? "duo" : "explorer_pro";
        setSelectedPlan(defaultPlan);
      }
      
      // Map backend variant to copy variant
      const backendVariant = variantRes.data.variant;
      let copyVariant = "A";
      if (backendVariant === "progress_stats") copyVariant = "B";
      else if (backendVariant === "duo_best_value") copyVariant = type === PaywallType.DUO_UPSELL ? "A" : "C";
      else if (backendVariant === "emotional_dream") copyVariant = "A";
      
      setVariant(copyVariant);
      setVariantLoaded(true);
      
      // Track impression
      trackPaywallImpression(backendVariant, triggerSource, content.trigger);
      trackMonetization(AnalyticsEvents.PAYWALL_SHOWN, {
        variant: backendVariant,
        triggerSource,
        featureSource: content.trigger,
        paywallType: type,
        smartDefaultsUsed: !!defaultsRes.data,
      });
    } catch (error) {
      console.debug("Paywall init error:", error);
      // Fallback
      const defaultPlan = type === PaywallType.DUO_UPSELL || hasPartner ? "duo" : "explorer_pro";
      setSelectedPlan(defaultPlan);
      setVariantLoaded(true);
    }
  };
  
  const fetchAnchoring = async (planId) => {
    try {
      const res = await axios.get(`${API}/pricing/anchoring/${planId}`);
      setAnchoring(res.data);
    } catch (error) {
      console.debug("Anchoring fetch error:", error);
    }
  };

  const handlePlanSelect = (plan) => {
    const originalPlan = selectedPlan;
    setSelectedPlan(plan);
    fetchAnchoring(plan);
    
    trackMonetization(plan === "duo" ? AnalyticsEvents.DUO_SELECTED : AnalyticsEvents.PLAN_VIEWED, {
      variant,
      planId: plan,
      paywallType: type,
      changedFromDefault: originalPlan !== plan,
    });
  };

  const handleBillingCycleChange = (cycle) => {
    const originalCycle = billingCycle;
    setBillingCycle(cycle);
    
    trackMonetization(cycle === "yearly" ? AnalyticsEvents.ANNUAL_SELECTED : AnalyticsEvents.MONTHLY_SELECTED, {
      variant,
      planId: selectedPlan,
      billingCycle: cycle,
      changedFromDefault: originalCycle !== cycle,
    });
  };

  const handleUpgrade = async () => {
    if (!selectedPlan) return;
    
    setLoading(true);
    try {
      trackMonetization(AnalyticsEvents.CTA_TAPPED, {
        variant,
        planId: selectedPlan,
        billingCycle,
        paywallType: type,
        triggerSource,
      });

      await trackConversion(AnalyticsEvents.CHECKOUT_STARTED, selectedPlan, billingCycle, {
        variant,
        triggerSource,
        featureSource: content.trigger,
      });

      const res = await axios.post(`${API}/subscription/checkout`, {
        plan_id: selectedPlan,
        billing_cycle: billingCycle,
      }, {
        headers: { origin: window.location.origin },
      });
      
      window.location.href = res.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start checkout");
      setLoading(false);
    }
  };

  const handleWebCheckout = async () => {
    if (!selectedPlan) return;
    setCheckoutError(null);

    console.log("[PaywallScreen] CTA tapped", { selectedPlan, billingCycle });

    // Telemetry (fire-and-forget)
    trackMonetization("paywall_cta_tapped", {
      variant,
      planId: selectedPlan,
      billingCycle,
      source: window.isNativeApp ? "ios_native" : "web",
    });
    axios.post(`${API}/subscription/telemetry`, {
      event: "stripe_checkout_opened",
      metadata: { plan_id: selectedPlan, billing_cycle: billingCycle, native: !!window.isNativeApp },
    }).catch(() => {});

    // Native app path: use bridge (async is OK since it's a native call)
    if (window.isNativeApp && (window.openSafariCheckout || window.ReactNativeWebView)) {
      setWebCheckoutLoading(true);
      try {
        const res = await axios.post(`${API}/subscription/checkout/mobile`, {
          plan_id: selectedPlan,
          billing_cycle: billingCycle,
        });
        const checkoutUrl = res.data.checkout_url;
        if (!checkoutUrl) throw new Error("No checkout URL");

        if (window.openSafariCheckout) {
          window.openSafariCheckout(checkoutUrl);
        } else if (window.ReactNativeWebView) {
          window.ReactNativeWebView.postMessage(JSON.stringify({
            type: "OPEN_SAFARI_CHECKOUT", url: checkoutUrl,
          }));
        }
      } catch (error) {
        const msg = error.response?.data?.detail || "Unable to open checkout. Please try again.";
        toast.error(msg);
        setCheckoutError(msg);
      } finally {
        setWebCheckoutLoading(false);
      }
      return;
    }

    // Web/Safari path: synchronous navigation via GET redirect endpoint
    // This MUST be synchronous from the user tap — no async gap — to avoid iOS popup blocker
    const token = axios.defaults.headers.common["Authorization"]?.replace("Bearer ", "");
    if (!token) {
      toast.error("Please log in to continue.");
      return;
    }

    // Navigate immediately — the server creates the Stripe session and 303-redirects
    window.location.href = `${API}/subscription/checkout/redirect?plan_id=${selectedPlan}&billing_cycle=${billingCycle}&token=${token}`;
  };

  const handleClose = () => {
    trackPaywallDismiss(variant, triggerSource);
    trackMonetization(AnalyticsEvents.PAYWALL_DISMISSED, {
      variant,
      paywallType: type,
      triggerSource,
    });
    onClose();
  };

  const handleRestore = async () => {
    try {
      trackMonetization(AnalyticsEvents.RESTORE_TAPPED, { variant, paywallType: type });
      const res = await axios.post(`${API}/subscription/restore`);
      if (res.data.restored_count > 0) {
        toast.success("Purchases restored!");
        onClose();
        window.location.reload();
      } else {
        toast.info("No previous purchases found");
      }
    } catch (error) {
      toast.error("Failed to restore purchases");
    }
  };

  const HeroIcon = content.heroIcon;
  const pricing = PRICING[selectedPlan] || PRICING.explorer_pro;
  const currentPrice = pricing[billingCycle];

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent zIndex={10002} className="max-w-lg p-0 gap-0 overflow-hidden bg-card border-none rounded-3xl shadow-2xl max-h-[95vh] overflow-y-auto">
        {/* Accessibility: Hidden title and description for screen readers */}
        <VisuallyHidden>
          <DialogTitle>{variantContent.headline}</DialogTitle>
          <DialogDescription>{variantContent.subheadline}</DialogDescription>
        </VisuallyHidden>
        
        {/* Loading state — prevents old variant flash */}
        {!variantLoaded ? (
          <div className={`relative bg-gradient-to-br ${content.heroGradient} px-6 py-20 flex items-center justify-center`}>
            <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          </div>
        ) : (
        <>
        {/* Hero Header */}
        <div className={`relative bg-gradient-to-br ${content.heroGradient} px-6 pt-10 pb-8 text-white overflow-hidden`}>
          {/* Background decoration */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-4 right-4 w-32 h-32 rounded-full bg-white/20 blur-2xl" />
            <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full bg-white/10 blur-3xl" />
            {/* Subtle grid pattern */}
            <div className="absolute inset-0" style={{
              backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255,255,255,0.1) 1px, transparent 0)`,
              backgroundSize: '24px 24px'
            }} />
          </div>
          
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors z-10"
            data-testid="paywall-close"
          >
            <X className="w-4 h-4" />
          </button>
          
          {/* Hero content */}
          <div className="relative z-10 text-center">
            {/* Icon */}
            <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-lg">
              <HeroIcon className="w-8 h-8" />
            </div>
            
            {/* Headline */}
            <h1 className="font-serif text-2xl sm:text-3xl font-bold mb-3 leading-tight">
              {variantContent.headline}
            </h1>
            
            {/* Subheadline */}
            <p className="text-white/80 text-sm sm:text-base leading-relaxed max-w-sm mx-auto">
              {variantContent.subheadline}
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-6">
          {/* Benefit Stack */}
          <div className="space-y-3 mb-6">
            {content.benefits.map((benefit, idx) => (
              <div 
                key={idx}
                className="flex items-center gap-3 p-2 rounded-xl hover:bg-accent/5 transition-colors"
              >
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${content.heroGradient} flex items-center justify-center flex-shrink-0 shadow-sm`}>
                  <benefit.icon className="w-5 h-5 text-white" />
                </div>
                <span className="text-sm font-medium text-foreground">{benefit.text}</span>
              </div>
            ))}
          </div>

          {/* Plan Selector */}
          <div className="space-y-3 mb-6">
            {/* Plan buttons */}
            <div className="flex gap-2">
              <button
                onClick={() => handlePlanSelect("explorer_pro")}
                className={`flex-1 p-3 rounded-xl border-2 transition-all ${
                  selectedPlan === "explorer_pro"
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/50"
                }`}
                data-testid="plan-select-explorer"
              >
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Sparkles className="w-4 h-4 text-primary" />
                  <span className="font-semibold text-sm">Explorer Pro</span>
                </div>
                <p className="text-xs text-muted-foreground">For solo explorers</p>
              </button>
              
              {hasPartner && (
                <button
                  onClick={() => handlePlanSelect("duo")}
                  className={`flex-1 p-3 rounded-xl border-2 transition-all relative ${
                    selectedPlan === "duo"
                      ? "border-secondary bg-secondary/5"
                      : "border-border hover:border-secondary/50"
                  }`}
                  data-testid="plan-select-duo"
                >
                  {content.savingsBadge && (
                    <span className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-secondary text-white text-[10px] font-semibold rounded-full">
                      {content.savingsBadge}
                    </span>
                  )}
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <Users className="w-4 h-4 text-secondary" />
                    <span className="font-semibold text-sm">Duo</span>
                  </div>
                  <p className="text-xs text-muted-foreground">For couples</p>
                </button>
              )}
            </div>

            {/* Billing cycle selector — web only, native shows link to website */}
            {isNativeApp ? (
              <div className="p-4 rounded-xl bg-muted/30 border border-border text-center">
                <p className="text-sm text-muted-foreground mb-1">Premium plans available on our website</p>
                <p className="text-xs text-primary font-medium">View plans on duolyst.co.uk</p>
              </div>
            ) : (
            <div className="flex gap-2">
              <button
                onClick={() => handleBillingCycleChange("monthly")}
                className={`flex-1 p-3 rounded-xl border-2 transition-all ${
                  billingCycle === "monthly"
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/30"
                }`}
                data-testid="billing-monthly"
              >
                <p className="font-semibold text-sm">Monthly</p>
                <p className="text-lg font-bold">{anchoring?.monthly_price || pricing.monthly.display}</p>
                <p className="text-xs text-muted-foreground">/month</p>
              </button>
              
              <button
                onClick={() => handleBillingCycleChange("yearly")}
                className={`flex-1 p-3 rounded-xl border-2 transition-all relative ${
                  billingCycle === "yearly"
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/30"
                }`}
                data-testid="billing-yearly"
              >
                <span className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[10px] font-bold rounded-full shadow-sm">
                  Best Value
                </span>
                <p className="font-semibold text-sm">Yearly</p>
                <p className="text-lg font-bold">{anchoring?.yearly_price || pricing.yearly.display}</p>
                <p className="text-xs text-muted-foreground">/year</p>
                {/* Price anchoring: show monthly equivalent */}
                <p className="text-xs text-emerald-600 font-semibold mt-1">
                  {anchoring?.yearly_monthly_equivalent || `£${(pricing.yearly.price / 12).toFixed(2)}`}/mo
                </p>
                {(anchoring?.savings_badge || pricing.yearly.savings) && (
                  <span className="inline-block mt-1 px-2 py-0.5 bg-emerald-100 dark:bg-emerald-900/30 rounded text-[10px] font-bold text-emerald-700 dark:text-emerald-300">
                    {anchoring?.savings_badge || pricing.yearly.savings}
                  </span>
                )}
              </button>
            </div>
            )}
            
            {/* Duo savings comparison — web only */}
            {!isNativeApp && selectedPlan === "duo" && anchoring?.vs_two_solo && (
              <div className="mt-2 p-2 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800 text-center">
                <p className="text-xs text-emerald-700 dark:text-emerald-300">
                  <span className="font-semibold">Premium for two</span> — Save {anchoring.vs_two_solo.savings_percent}% vs 2 separate Explorer Pro plans
                </p>
              </div>
            )}
            
            {/* Emotional framing — web only */}
            {!isNativeApp && anchoring?.emotional_framing && billingCycle === "yearly" && (
              <p className="text-center text-xs text-muted-foreground mt-2 italic">
                {anchoring.emotional_framing}
              </p>
            )}
          </div>

          {/* Primary CTA — always routes to external web checkout */}
          <Button
            onClick={handleWebCheckout}
            disabled={webCheckoutLoading || !selectedPlan}
            className={`w-full h-14 text-base font-semibold rounded-2xl shadow-lg transition-all bg-gradient-to-r ${content.heroGradient} hover:opacity-90 text-white`}
            data-testid="paywall-cta"
          >
            {webCheckoutLoading ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                <Globe className="w-5 h-5 mr-2" />
                {content.cta.upgrade}
              </>
            )}
          </Button>

          {/* Error state with retry */}
          {checkoutError && (
            <div className="mt-2 p-3 bg-destructive/10 rounded-xl text-center">
              <p className="text-sm text-destructive mb-2">Unable to open checkout. Please try again.</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setCheckoutError(null); handleWebCheckout(); }}
                data-testid="paywall-retry-btn"
              >
                Retry
              </Button>
            </div>
          )}
        </div>

        {/* Trust Footer */}
        <div className="px-6 pb-6 pt-2 border-t border-border/50">
          {/* Restore Purchases — required by App Store */}
          <button
            onClick={handleRestore}
            className="w-full text-center text-xs text-primary hover:text-primary/80 transition-colors py-2 mb-2"
            data-testid="restore-purchases-btn"
          >
            Restore Purchases
          </button>
          <div className="flex items-center justify-center gap-4 text-xs">
            <a
              href="https://duolyst.co.uk/pricing"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
              data-testid="manage-premium-link"
            >
              Manage Premium on duolyst.co.uk
            </a>
            <span className="text-border">|</span>
            <a href="/terms" className="text-muted-foreground hover:text-foreground">
              Terms
            </a>
            <span className="text-border">|</span>
            <a href="/privacy" className="text-muted-foreground hover:text-foreground">
              Privacy
            </a>
          </div>
          
        </div>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ==================== HOOK FOR TRIGGERING PAYWALLS ====================

export const usePaywall = () => {
  const [paywallState, setPaywallState] = useState({
    isOpen: false,
    type: PaywallType.WORLD_MAP,
    triggerSource: "locked_feature",
  });

  const showPaywall = useCallback((type, triggerSource = "locked_feature") => {
    setPaywallState({
      isOpen: true,
      type,
      triggerSource,
    });
  }, []);

  const hidePaywall = useCallback(() => {
    setPaywallState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  // Convenience methods for each paywall type
  const showWorldMapPaywall = useCallback((source = "map_lock") => {
    showPaywall(PaywallType.WORLD_MAP, source);
  }, [showPaywall]);

  const showRankPaywall = useCallback((source = "rank_lock") => {
    showPaywall(PaywallType.TRAVEL_RANK, source);
  }, [showPaywall]);

  const showMemoryPaywall = useCallback((source = "memory_limit") => {
    showPaywall(PaywallType.MEMORY_VAULT, source);
  }, [showPaywall]);

  const showDuoPaywall = useCallback((source = "partner_link") => {
    showPaywall(PaywallType.DUO_UPSELL, source);
  }, [showPaywall]);

  return {
    paywallState,
    showPaywall,
    hidePaywall,
    showWorldMapPaywall,
    showRankPaywall,
    showMemoryPaywall,
    showDuoPaywall,
  };
};

export default PaywallScreen;
